import {
  meta,
  aggregate,
  videos,
  thumbnails,
} from "@/kooleyy/accessors/client";
import { useStandardQuery } from "@/kooleyy/lib/query";

export const useClientVideos = ({ id }) => {
  const { data: clientVideos, isPending: clientVideosPending } =
    useStandardQuery({
      queryKey: [`client-${id}-video-newest-first`],
      queryFn: () => videos(id, "video"),
    });

  return [clientVideos?.hits, clientVideosPending];
};

export const useClientThumbnails = ({ id }) => {
  const { data: clientThumbnails, isPending: clientThumbnailsPending } =
    useStandardQuery({
      queryKey: [`client-${id}-thumbnail-newest-first`],
      queryFn: () => thumbnails(id, "all"),
    });

  return [clientThumbnails?.hits, clientThumbnailsPending];
};

export const useClientShorts = ({ id }) => {
  const { data: clientShorts, isPending: clientShortsPending } =
    useStandardQuery({
      queryKey: [`client-${id}-short-newest-first`],
      queryFn: () => videos(id, "short"),
    });

  return [clientShorts?.hits, clientShortsPending];
};

export const useClientContentTotals = ({ id }) => {
  const [videoData, videoPending] = useClientVideos({ id });
  const [thumbnailData, thumbnailPending] = useClientThumbnails({ id });
  const [shortData, shortPending] = useClientShorts({ id });

  if (videoPending || thumbnailPending || shortPending) return [{}, true];

  const totals = {
    videos: videoData?.total?.value,
    thumbnails: thumbnailData?.total?.value,
    shorts: shortData?.total?.value,
  };

  return [totals, false];
};

export const useClientInformation = ({ id }) => {
  const {
    data: clientData,
    isPending: clientPending,
    status,
  } = useStandardQuery({
    queryKey: [`client-${id}-meta`],
    queryFn: () => meta(id),
  });

  return [clientData?.response, clientPending, status];
};

export const useClientAggregate = ({ id }) => {
  const {
    data: aggregateData,
    isPending: aggregateDataPending,
    status,
  } = useStandardQuery({
    queryKey: [`client-${id}-meta`],
    queryFn: () => aggregate(id),
  });

  return [aggregateData?.response, aggregateDataPending, status];
};

export const useClientMeta = ({ id }) => {
  const [clientData, clientPending] = useClientInformation({ id });

  const [clientAggregateData, clientAggregatePending] = useClientAggregate({
    id,
  });

  const [totals, totalsPending] = useClientContentTotals({ id });

  if (clientAggregatePending || clientPending || totalsPending)
    return [{}, true];

  const response = {
    id,
    name: clientData?.name,
    subscribers: clientData?.channelSocial?.subscribers,
    totals: {
      videos: totals?.videos,
      thumbnails: totals?.thumbnails,
      shorts: totals?.shorts,
      views: clientAggregateData?.views,
      allContent: clientData?.channelSocial?.allVideos,
    },
    oldest: clientAggregateData?.oldestVideo,
    avatar: clientData?.thumbnail?.high?.url,
    newest: clientAggregateData?.newestVideo,
    show: [
      totals?.videos > 0 && "videos",
      totals?.thumbnails > 0 && "thumbnails",
      totals?.shorts > 0 && "shorts",
    ].filter((s) => s),
  };

  return [response, false];
};
